import { useRef, useState, useEffect, Dispatch, SetStateAction, MutableRefObject } from 'react';

interface IUseDisplayValueReturn<TDisplay> {
	displayValue: TDisplay | null;
	setDisplayValue: Dispatch<SetStateAction<TDisplay | null>>;
	isInternalChange: MutableRefObject<boolean>;
}

/**
 * Manages the internal display state for GoogleAddress.
 *
 * Keeps displayValue in sync with external value prop changes
 * while skipping updates triggered by the component's own onChange.
 */
export function useDisplayValue<TNative, TDisplay>(
	value: TNative | null | undefined,
	convert: (val: TNative) => TDisplay | undefined,
): IUseDisplayValueReturn<TDisplay> {
	const isInternalChange = useRef(false);

	const toDisplay = (val: TNative | null | undefined): TDisplay | null =>
		val ? convert(val) ?? null : null;

	const [displayValue, setDisplayValue] = useState<TDisplay | null>(() => toDisplay(value));

	useEffect(() => {
		if (isInternalChange.current) {
			isInternalChange.current = false;
			return;
		}
		setDisplayValue(toDisplay(value));
	}, [value]);

	return { displayValue, setDisplayValue, isInternalChange };
}
